import { Label } from 'cpool-components/general/Label';
import { Checkbox } from 'cpool-components/input/Checkbox';
import { RadioButton } from 'cpool-components/input/RadioButton';
import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import type { Path } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema';
import { cn } from '~/utils/cnTailwind';
import { normalizeTextInput } from '~/utils/normalizeTextInput';

type Option = {
    label: string;
    value: string;
};

type BaseProps = {
    disabled?: boolean;
    label: string;
    labelClassName?: string;
    name: Path<SchemaRealEstatesForm>;
};

type OptionGroupProps = BaseProps & {
    options: Option[];
};

type CheckboxGroupProps = OptionGroupProps & {
    extraContent?: React.ReactNode;
};

type OptionGroupFieldsetProps = {
    children: React.ReactNode;
    className?: string;
    label: string;
};

export const OptionGroupFieldset: React.FC<OptionGroupFieldsetProps> = ({ children, className, label }) => (
    <fieldset
        className={cn('w-full min-w-0 rounded-lg border border-grey-outline-light bg-white px-5 py-5', className)}
    >
        <legend className="float-left mb-3 w-full p-0 text-sm font-bold text-midnight">{label}</legend>
        {children}
    </fieldset>
);

const OPTION_CLASS_NAME = 'min-h-8 w-full py-1';

export const OptionGroupGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="clear-both grid grid-cols-1 items-center gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-4">
        {children}
    </div>
);

export const FormRadioGroup: React.FC<OptionGroupProps> = ({ disabled, label, name, options }) => (
    <Controller
        name={name}
        render={({ field }) => (
            <OptionGroupFieldset label={label}>
                <OptionGroupGrid>
                    {options.map((option) => (
                        <RadioButton
                            key={option.value}
                            id={`${name}-${option.value}`}
                            name={name}
                            label={option.label}
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={() => field.onChange(option.value)}
                            disabled={disabled}
                            wrapperClassName={OPTION_CLASS_NAME}
                        />
                    ))}
                </OptionGroupGrid>
            </OptionGroupFieldset>
        )}
    />
);

export const FormBooleanSegmentedField: React.FC<{
    disabled?: boolean;
    label: string;
    labelClassName?: string;
    name: Path<SchemaRealEstatesForm>;
}> = ({ disabled, label, labelClassName = 'h-8 w-full shrink-0 justify-start sm:w-[175px]', name }) => {
    const { t } = useTranslation();
    return (
        <Controller
            name={name}
            render={({ field }) => {
                let value: 'no' | 'yes' | null = null;
                if (field.value === true) value = 'yes';
                if (field.value === false) value = 'no';

                return (
                    <div className="flex flex-col items-start gap-2.5 sm:flex-row">
                        <Label className={labelClassName}>{label}</Label>
                        <SegmentedControl<'yes' | 'no'>
                            id={name}
                            ariaLabel={label}
                            options={[
                                { label: t('VALUES.YES'), value: 'yes' },
                                { label: t('VALUES.NO'), value: 'no' },
                            ]}
                            value={value ?? 'no'}
                            onChange={(nextValue) => field.onChange(nextValue === 'yes')}
                            disabled={disabled}
                            wide
                        />
                    </div>
                );
            }}
        />
    );
};

export const FormCheckboxGroup: React.FC<CheckboxGroupProps> = ({ disabled, extraContent, label, name, options }) => (
    <Controller
        name={name}
        render={({ field }) => {
            const selectedValues = Array.isArray(field.value)
                ? field.value.filter((value): value is string => typeof value === 'string')
                : [];
            return (
                <OptionGroupFieldset label={label}>
                    <OptionGroupGrid>
                        {options.map((option) => (
                            <div key={option.value} className={`flex items-center ${OPTION_CLASS_NAME}`}>
                                <Checkbox
                                    id={`${name}-${option.value}`}
                                    label={option.label}
                                    checked={selectedValues.includes(option.value)}
                                    onChange={(event) => {
                                        field.onChange(
                                            event.target.checked
                                                ? [...selectedValues, option.value]
                                                : selectedValues.filter((value) => value !== option.value),
                                        );
                                    }}
                                    disabled={disabled}
                                />
                            </div>
                        ))}
                    </OptionGroupGrid>
                    {extraContent ? <div className="clear-both mt-3">{extraContent}</div> : null}
                </OptionGroupFieldset>
            );
        }}
    />
);

export const FormTextArea: React.FC<BaseProps> = ({
    disabled,
    label,
    labelClassName = 'min-h-8 w-[175px] shrink-0 justify-start',
    name,
}) => {
    const { control } = useFormContext<SchemaRealEstatesForm>();
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState: { error } }) => (
                <div className="flex items-start gap-2.5">
                    <Label htmlFor={name} className={labelClassName}>
                        {label}
                    </Label>
                    <div className="flex flex-1 flex-col">
                        <textarea
                            id={name}
                            value={(field.value as string | null | undefined) ?? ''}
                            onChange={(event) => field.onChange(event.target.value || null)}
                            onBlur={() => {
                                const normalized = normalizeTextInput(field.value);
                                if (normalized !== field.value) field.onChange(normalized);
                            }}
                            disabled={disabled}
                            className="min-h-20 w-full resize-y rounded border border-grey-dark bg-white px-3 py-2 text-sm text-midnight focus:outline-none focus:ring-2 focus:ring-blue-focus disabled:cursor-not-allowed disabled:bg-surface-light"
                        />
                        {error?.message && <span className="mt-1 text-[10px] text-alert-error">{error.message}</span>}
                    </div>
                </div>
            )}
        />
    );
};
